import {
  BrandMark,
  Card,
  ColorSwatchPicker,
  Dropzone,
  FormField,
  Inline,
  Input,
  LinkButton,
  Stack,
  Text,
} from '@hkdevs/design-system';
import { useEffect, useMemo, useState } from 'react';
import type {
  AdminSettings,
  LogoVariant,
  SettingsUpdate,
  WhiteLabelChanges,
} from '../../api/queries/settings';

/** The largest logo the API takes. */
export const LOGO_MAX_BYTES = 1024 * 1024;

/** A logo while editing: as saved, a new file, or back to the deployment's. */
export type LogoDraft = { kind: 'keep' } | { kind: 'file'; file: File } | { kind: 'default' };

/** The white-label overrides being edited. A blank name or a null accent is no override. */
export interface WhiteLabelDraft {
  brandName: string;
  productName: string;
  accent: string | null;
  logos: Record<LogoVariant, LogoDraft>;
}

const VARIANTS: readonly LogoVariant[] = ['light', 'dark'];

export function draftFrom(settings: AdminSettings): WhiteLabelDraft {
  const { overrides } = settings;
  return {
    brandName: overrides.brandName ?? '',
    productName: overrides.productName ?? '',
    accent: overrides.brandAccent,
    logos: { light: { kind: 'keep' }, dark: { kind: 'keep' } },
  };
}

/** What saving the draft changes: only the fields that differ from the overrides. */
export function changesFrom(draft: WhiteLabelDraft, settings: AdminSettings): WhiteLabelChanges {
  const { overrides } = settings;
  const fields: SettingsUpdate = {};
  const brandName = draft.brandName.trim() || null;
  const productName = draft.productName.trim() || null;
  if (brandName !== overrides.brandName) fields.brandName = brandName;
  if (productName !== overrides.productName) fields.productName = productName;
  if (draft.accent !== overrides.brandAccent) fields.brandAccent = draft.accent;
  const logos: WhiteLabelChanges['logos'] = {};
  for (const variant of VARIANTS) {
    const logo = draft.logos[variant];
    if (logo.kind === 'file') logos[variant] = logo.file;
    if (logo.kind === 'default' && overrides.logo[variant] !== null) logos[variant] = null;
  }
  return { fields, logos };
}

export const hasChanges = ({ fields, logos }: WhiteLabelChanges) =>
  Object.keys(fields).length > 0 || Object.keys(logos).length > 0;

/** An object URL for a picked file, revoked when it changes or unmounts. */
function useObjectUrl(file: File | undefined) {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  return url;
}

const LOGO_LABELS: Record<LogoVariant, string> = {
  light: 'Logo for light surfaces',
  dark: 'Logo for dark and ink surfaces',
};

function LogoField({
  variant,
  draft,
  settings,
  brandName,
  onChange,
}: {
  variant: LogoVariant;
  draft: LogoDraft;
  settings: AdminSettings;
  brandName: string;
  onChange: (logo: LogoDraft) => void;
}) {
  const [error, setError] = useState<string>();
  const picked = useObjectUrl(draft.kind === 'file' ? draft.file : undefined);
  const saved = settings.overrides.logo[variant];
  const fallback = settings.defaults.logo[variant];
  const shown =
    draft.kind === 'file' ? picked : draft.kind === 'keep' ? (saved ?? fallback) : fallback;
  const overridden = draft.kind === 'file' || (draft.kind === 'keep' && saved !== null);

  return (
    <Stack gap={2}>
      <Text as="p">{LOGO_LABELS[variant]}</Text>
      <Inline gap={3}>
        <Card {...(variant === 'dark' ? { 'data-surface': 'inverse' } : {})}>
          {shown ? (
            <BrandMark size="sm" name={brandName} logo={shown} letterMark={false} />
          ) : (
            <Text size="meta" tone="muted">
              The brand’s own
            </Text>
          )}
        </Card>
        {overridden && (
          <LinkButton tone="neutral" onClick={() => onChange({ kind: 'default' })}>
            Use the default
          </LinkButton>
        )}
      </Inline>
      <Dropzone
        accept=".png,.svg,image/png,image/svg+xml"
        title={`Drop a PNG or SVG for ${variant === 'light' ? 'light' : 'dark'} surfaces`}
        description="or browse · at most 1 MB"
        browseLabel="Browse"
        invalid={error !== undefined}
        onFiles={([file]) => {
          if (!file) return;
          if (file.size > LOGO_MAX_BYTES) {
            setError(`${file.name} is larger than 1 MB.`);
            return;
          }
          setError(undefined);
          onChange({ kind: 'file', file });
        }}
        onReject={() => setError(undefined)}
      />
      {error && (
        <Text as="p" tone="danger" role="alert">
          {error}
        </Text>
      )}
    </Stack>
  );
}

/** The white-label section's fields while editing (SettingsSection's form). */
export function WhiteLabelForm({
  draft,
  settings,
  packName,
  onChange,
}: {
  draft: WhiteLabelDraft;
  settings: AdminSettings;
  /** The brand pack's own brand name, the last fallback. */
  packName: string;
  onChange: (draft: WhiteLabelDraft) => void;
}) {
  const { defaults } = settings;
  const brandDefault = defaults.brandName ?? packName;
  const productDefault = defaults.productName ?? 'Payments Console';
  const set = (patch: Partial<WhiteLabelDraft>) => onChange({ ...draft, ...patch });

  return (
    <Stack gap={5}>
      <FormField label="Brand name" hint={`Leave blank for the default, ${brandDefault}.`}>
        <Input
          value={draft.brandName}
          placeholder={brandDefault}
          maxLength={80}
          onChange={(event) => set({ brandName: event.target.value })}
        />
      </FormField>
      <FormField label="Product name" hint={`Leave blank for the default, ${productDefault}.`}>
        <Input
          value={draft.productName}
          placeholder={productDefault}
          maxLength={80}
          onChange={(event) => set({ productName: event.target.value })}
        />
      </FormField>
      <Stack gap={2}>
        <ColorSwatchPicker
          value={draft.accent ?? defaults.brandAccent}
          onChange={(accent) => set({ accent })}
          allowCustom
        />
        <Inline gap={3}>
          <Text size="meta" tone="muted">
            {defaults.brandAccent
              ? `The default is ${defaults.brandAccent}.`
              : 'The default is the brand’s own.'}
          </Text>
          {draft.accent !== null && (
            <LinkButton tone="neutral" onClick={() => set({ accent: null })}>
              Use the default
            </LinkButton>
          )}
        </Inline>
      </Stack>
      {VARIANTS.map((variant) => (
        <LogoField
          key={variant}
          variant={variant}
          draft={draft.logos[variant]}
          settings={settings}
          brandName={draft.brandName.trim() || brandDefault}
          onChange={(logo) => set({ logos: { ...draft.logos, [variant]: logo } })}
        />
      ))}
    </Stack>
  );
}
